import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, SvgIcon, Typography } from '@mui/material';

import { SpecialNeedsEnum } from '@mopla/data-models';
import AccessibleOutlined from '@mopla/ui/icons/AccessibleOutlined.svg';
import LuggageOutlined from '@mopla/ui/icons/LuggageOutlined.svg';
import ProfileOutlined from '@mopla/ui/icons/ProfileOutlined.svg';

import { InfoWrapper } from './styled';

interface IBookingPassengersProps {
	passengerName: string;
	phoneNumber: string;
	additionalPassengerCount?: number;
	luggageCount?: number;
	specialNeeds?: SpecialNeedsEnum;
	toddlersCount?: number;
}

const getWheelchairText = (specialNeeds?: SpecialNeedsEnum) => {
	if (specialNeeds === SpecialNeedsEnum.WHEELCHAIR_TYPE_1) {
		return 'summary.passengers.wheelchair_seated';
	}
	if (specialNeeds === SpecialNeedsEnum.WHEELCHAIR_TYPE_2) {
		return 'summary.passengers.wheelchair_luggage';
	}
	return null;
};

export const BookingPassengers: FC<IBookingPassengersProps> = ({
	passengerName,
	phoneNumber,
	additionalPassengerCount = 0,
	luggageCount = 0,
	specialNeeds,
	toddlersCount = 0,
}) => {
	const { t } = useTranslation('booking');
	const wheelchairText = getWheelchairText(specialNeeds);

	return (
		<Stack spacing={2}>
			<InfoWrapper>
				<SvgIcon component={ProfileOutlined} fontSize="medium" />
				<Stack spacing={0.5}>
					<Stack direction="row" flexWrap="wrap" spacing={0.5}>
						<Typography
							variant="b2-m"
							whiteSpace="nowrap"
							data-testid="bookingPassengers-name-text"
						>
							{passengerName}
						</Typography>
						{(additionalPassengerCount > 0 || toddlersCount > 0) && (
							<Stack direction="row" spacing={0.5}>
								{additionalPassengerCount > 0 && (
									<Typography
										variant="b2"
										color="solutions.600"
										whiteSpace="nowrap"
										data-testid="bookingPassengers-additionalPassengers-text"
									>
										{t('summary.passengers.additional', {
											count: additionalPassengerCount,
										})}
									</Typography>
								)}
								{toddlersCount > 0 && (
									<Typography
										variant="b2"
										color="solutions.600"
										whiteSpace="nowrap"
										data-testid="bookingPassengers-toddlers-text"
									>
										{t('summary.passengers.toddlers', {
											count: toddlersCount,
										})}
									</Typography>
								)}
							</Stack>
						)}
					</Stack>

					<Typography
						variant="b2"
						color="solutions.600"
						data-testid="bookingPassengers-phone-text"
					>
						{phoneNumber}
					</Typography>
				</Stack>
			</InfoWrapper>
			{(luggageCount > 0 || wheelchairText) && (
				<Stack spacing={1}>
					{luggageCount > 0 && (
						<InfoWrapper>
							<SvgIcon component={LuggageOutlined} fontSize="medium" />

							<Typography
								variant="b2-m"
								data-testid="bookingPassengers-luggage-text"
							>
								{t('summary.passengers.luggage', { count: luggageCount })}
							</Typography>
						</InfoWrapper>
					)}
					{wheelchairText && (
						<InfoWrapper>
							<SvgIcon component={AccessibleOutlined} fontSize="medium" />

							<Typography
								variant="b2-m"
								data-testid="bookingPassengers-wheelchair-text"
							>
								{t(wheelchairText)}
							</Typography>
						</InfoWrapper>
					)}
				</Stack>
			)}
		</Stack>
	);
};
